import { useEffect, useRef } from 'react'
import { EditorView, basicSetup } from 'codemirror'
import { EditorState } from '@codemirror/state'
import { sql } from '@codemirror/lang-sql'
import { indentUnit } from '@codemirror/language'
import { keymap, placeholder as placeholderExtension } from '@codemirror/view'
import { defaultKeymap, indentWithTab } from '@codemirror/commands'

interface SqlCodeEditorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  minLines?: number
}

const LINE_HEIGHT = 20

/**
 * Plain CodeMirror editor configured for SQL. Mirrors the structure of
 * Shared/components/CodeMirrorEditor but typed, SQL-specific, and isolated to
 * the AnalyticsDashboard bundle.
 */
export default function SqlCodeEditor({
  value,
  onChange,
  disabled = false,
  placeholder,
  minLines = 12,
}: SqlCodeEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const viewRef = useRef<EditorView | null>(null)
  // Keep the latest onChange in a ref so the editor's update listener never
  // captures a stale closure (the editor is created once on mount).
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  useEffect(() => {
    if (!containerRef.current) return

    const extensions = [
      basicSetup,
      sql(),
      keymap.of([...defaultKeymap, indentWithTab]),
      indentUnit.of('  '),
      EditorView.lineWrapping,
      EditorView.editable.of(!disabled),
      EditorView.updateListener.of(update => {
        if (update.docChanged) onChangeRef.current(update.state.doc.toString())
      }),
      EditorView.theme({
        '&': {
          fontSize: '13px',
          minHeight: `${minLines * LINE_HEIGHT}px`,
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          backgroundColor: '#ffffff',
        },
        '&.cm-focused': {
          outline: 'none',
          borderColor: '#3b82f6',
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.15)',
        },
        '.cm-scroller': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          lineHeight: `${LINE_HEIGHT}px`,
        },
        '.cm-content': { padding: '8px 0' },
        '.cm-gutters': { backgroundColor: '#f8fafc', color: '#94a3b8', border: 'none' },
        '.cm-activeLine': { backgroundColor: 'rgba(241, 245, 249, 0.4)' },
        '.cm-activeLineGutter': { backgroundColor: 'rgba(241, 245, 249, 0.4)' },
      }),
    ]

    if (placeholder) extensions.push(placeholderExtension(placeholder))

    const state = EditorState.create({ doc: value, extensions })
    const view = new EditorView({ state, parent: containerRef.current })
    viewRef.current = view

    return () => {
      view.destroy()
      viewRef.current = null
    }
    // We deliberately create the editor once; subsequent value changes are
    // synced via the effect below. `placeholder`, `minLines`, `disabled` are
    // static for our usage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync external value changes (e.g. parent reset) without recreating the
  // editor and losing cursor/history.
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const current = view.state.doc.toString()
    if (current === value) return
    view.dispatch({ changes: { from: 0, to: current.length, insert: value } })
  }, [value])

  return <div ref={containerRef} />
}
